import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  ApartmentOutlined,
  FactCheckOutlined,
  ReceiptLongOutlined,
  SavingsOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Loader from "../../../UiComponents/Loader/Loader";
import { getBriskV2Config, getBriskV2WalletSummary } from "../../../Service/BriskV2Service";
import { BRISK_V2_HISTORY_PATH, BRISK_V2_REPORT_PATH } from "../../../Shared/briskV2Flag";
// the Dashboard's own cards (owner, 2026-11-03: "Dashboard look" for both wallet pages)
import { KpiCard, TabbedCard } from "../../Dashboard/v2/parts";
import BriskLedgerTable from "./BriskLedgerTable";
import { MismatchAlert } from "./BriskWalletParts";
import { rupees } from "./briskWalletUtils";

const GREEN = "#067647";
const RED = "#B42318";
const icon = (Icon) => <Icon sx={{ fontSize: 18 }} />;

/** The agency table's header row in the Dashboard's light navy. */
const AGENCY_TABLE_SX = {
  "& th": { backgroundColor: "#F3F6FB", color: "#1E3A5F", fontWeight: 700, fontSize: 12.5, borderBottom: "1px solid #D5DFEC" },
  "& td": { fontSize: 13, color: "#101828" },
  "& tbody tr:last-of-type td": { borderBottom: 0 },
};

/**
 * Brisk Wallet: the agency's Company Wallet "Brisk" (the super admin sees every agency). Dashboard look (2026-11-03):
 * small KPI cards, then the tables as tabs (Recent transactions; the super admin also Agency balances).
 */
const BriskAgencyWallet = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [apiEnv, setApiEnv] = useState("");
  const [problem, setProblem] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("transactions"); // the tables are tabs (owner, 2026-11-03)
  const [ledgerTotal, setLedgerTotal] = useState(null);
  const superAdmin = !!(summary && summary.superAdmin);
  const agencies = (summary && summary.rows) || [];
  const wallet = superAdmin ? summary && summary.total : summary && summary.agency;

  const loadSummary = useCallback(async () => {
    setLoading(true);
    const [configResult, summaryResult] = await Promise.all([getBriskV2Config(), getBriskV2WalletSummary()]);
    setLoading(false);
    if (configResult.error) {
      setProblem(configResult.message || "Brisk wallet settings could not be read");
      return;
    }
    if (summaryResult.error) {
      setProblem(summaryResult.message || "The Brisk wallet could not be read");
      return;
    }
    setApiEnv(configResult.data && configResult.data.apiEnv);
    setSummary(summaryResult.data);
    setProblem("");
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refreshKey]);

  const belowMinimum = agencies.filter((a) => a.belowMinimum).length;
  const mismatched = agencies.filter((a) => !a.matches).length;
  const ledger = !wallet
    ? { value: "—", hint: "", accent: "#98A2B3" }
    : wallet.matches
      ? { value: "Matches", hint: superAdmin ? "Every agency's history adds up" : "The balance is its history", accent: GREEN }
      : { value: "Mismatch", hint: superAdmin ? `${mismatched} ${mismatched === 1 ? "agency" : "agencies"}, off by ${rupees(wallet.ledgerDifference)}` : `Off by ${rupees(wallet.ledgerDifference)}`, accent: RED };

  const cards = superAdmin
    ? [
        { key: "balance", label: "Brisk Balance (all agencies)", value: rupees(wallet && wallet.balance), hint: "Every agency's Brisk wallet together", icon: AccountBalanceWalletOutlined },
        // a count, not money (it used to show "₹2.00" for two agencies)
        { key: "agencies", label: "Agencies", value: String(agencies.length), hint: "Included in the balance", icon: ApartmentOutlined },
        {
          key: "minimum",
          label: "Below Minimum",
          value: String(belowMinimum),
          hint: belowMinimum ? "Agencies to top up" : "Every agency is above its minimum",
          icon: SavingsOutlined,
          accent: belowMinimum ? RED : GREEN,
        },
        { key: "ledger", label: "Ledger Check", ...ledger, icon: FactCheckOutlined },
      ]
    : [
        { key: "balance", label: "Brisk Balance", value: rupees(wallet && wallet.balance), hint: "Your Company Wallet \"Brisk\"", icon: AccountBalanceWalletOutlined },
        { key: "minimum", label: "Minimum Balance", value: rupees(wallet && wallet.minimum), hint: "You are warned at or below this", icon: SavingsOutlined },
        {
          key: "status",
          label: "Wallet Status",
          value: wallet && wallet.belowMinimum ? "Below minimum" : "In good standing",
          hint: wallet && wallet.belowMinimum ? "Top up the Brisk wallet" : "Above the minimum",
          icon: VerifiedUserOutlined,
          accent: wallet && wallet.belowMinimum ? RED : GREEN,
        },
        { key: "ledger", label: "Ledger Check", ...ledger, icon: FactCheckOutlined },
      ];

  return (
    <div style={{ margin: "1px" }}>
      {loading && <Loader />}
      <Grid container className="Master_Card_Container">
        <Grid container alignItems="center" justifyContent="space-between" className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={5}>
            <Typography className="Master_Card_Tittle">
              Brisk Wallet
              {apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={7}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent={{ xs: "flex-start", sm: "flex-end" }} mt={{ xs: 2, sm: 0 }}>
              <Button className="Master_Card_Filter_Button" onClick={() => navigate(BRISK_V2_HISTORY_PATH)}>
                Transaction History
              </Button>
              <Button className="Master_Card_Filter_Button" onClick={() => navigate(BRISK_V2_REPORT_PATH)}>
                Transaction Report
              </Button>
              <Button className="Master_Card_Filter_Button" disabled={loading} onClick={() => setRefreshKey((key) => key + 1)}>
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, mb: 2 }} />
        </Grid>

        {problem && (
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            {problem}
          </Alert>
        )}

        {summary && (
          <>
            <MismatchAlert wallet={wallet} />
            <Grid container spacing={1.5} sx={{ mb: 2 }} data-testid="brisk-wallet-cards">
              {cards.map((c) => (
                <Grid item xs={12} sm={6} md={3} key={c.key}>
                  <KpiCard compact testId={`brisk-kpi-${c.key}`} label={c.label} value={c.value} hint={c.hint} accent={c.accent} icon={icon(c.icon)} />
                </Grid>
              ))}
            </Grid>

          </>
        )}

        <TabbedCard
          testId="brisk-wallet-tabs"
          value={superAdmin ? tab : "transactions"}
          onChange={setTab}
          tabs={[
            { value: "transactions", label: "Recent transactions", count: ledgerTotal, icon: icon(ReceiptLongOutlined) },
            ...(superAdmin ? [{ value: "agencies", label: "Agency balances", count: agencies.length, icon: icon(ApartmentOutlined) }] : []),
          ]}
          sx={{ width: "100%" }}
        >
          {superAdmin && tab === "agencies" ? (
            <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
              <TableContainer sx={{ border: "1px solid #EAECF0", borderRadius: "10px" }}>
                <Table size="small" sx={AGENCY_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Agency</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="right">Minimum</TableCell>
                      <TableCell>Wallet status</TableCell>
                      <TableCell>Ledger check</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agencies.map((agency) => (
                      <TableRow key={agency.clientId}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{agency.name || agency.email || agency.clientId}</Typography>
                          {agency.name && agency.email && <Typography sx={{ fontSize: 12, color: "#667085" }}>{agency.email}</Typography>}
                        </TableCell>
                        <TableCell align="right">{rupees(agency.balance)}</TableCell>
                        <TableCell align="right">{rupees(agency.minimum)}</TableCell>
                        <TableCell>
                          {agency.belowMinimum ? <Chip size="small" color="warning" label="Below minimum" /> : <Chip size="small" color="success" label="OK" />}
                        </TableCell>
                        <TableCell>
                          {agency.matches ? <Chip size="small" color="success" label="Matches" /> : <Chip size="small" color="error" label="Mismatch" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <BriskLedgerTable query={{}} showAgency={superAdmin} refreshKey={refreshKey} onLoaded={(data) => setLedgerTotal(data && typeof data.total === "number" ? data.total : null)} />
          )}
        </TabbedCard>
      </Grid>
    </div>
  );
};

export default BriskAgencyWallet;
